import React from "react";

import { makeGet } from "@culturehq/client";
import { Button, Loader, Pagination, Table, Warning } from "@culturehq/components";

import { ExternalMapper, ExternalMapperValue } from "chq";

import { useFetch, usePagination } from "app/hooks";
import { t, Trans } from "app/utils";
import PageHeader from "components/display/PageHeader";

import MapperModal from "./mappers/MapperModal";
import MapperRow from "./mappers/MapperRow";
import { createEntity, deleteEntity, updateEntity } from "./mutations";

const createExternalMapperValue = createEntity("externalMapperValues", "value");
const updateExternalMapperValue = updateEntity("externalMapperValues");
const deleteExternalMapperValue = deleteEntity("externalMapperValues");

interface JobMappingsProps {
  externalMapper: ExternalMapper;
}

const JobMappings: React.FC<JobMappingsProps> = ({ externalMapper }) => {
  const [page, onPageClick] = usePagination();
  const { id, label } = externalMapper;

  const {
    fetching,
    json: { externalMapperValues = [], pagination = {} },
    onUpdate: onExternalMapperValueUpdate
  } = useFetch(
    () => makeGet("/external_mapper_values", { page: page || 1, external_mapper_id: id } as any),
    [page]
  );

  const onCreate = (value: ExternalMapperValue) => (
    onExternalMapperValueUpdate(createExternalMapperValue(value))
  );
  const onUpdate = (value: ExternalMapperValue) => (
    onExternalMapperValueUpdate(updateExternalMapperValue(value))
  );
  const onDelete = (value: ExternalMapperValue) => (
    onExternalMapperValueUpdate(deleteExternalMapperValue(value))
  );

  return (
    <div className="padding-left-15px padding-right-15px">
      <PageHeader title={t(`Manage ${label}`)}>
        <Trans>
          Below is a list of all the {label}{" "}at your organization
          created by admins or users in your organization. Users can also create a new
          {" "}{label}.
        </Trans>
      </PageHeader>
      <MapperModal
        externalMapper={externalMapper}
        onCreate={onCreate}
        onUpdate={onUpdate}
      >
        {onTrigger => (
          <Button icon="plus" primary onClick={onTrigger}>
            <Trans>Add {label}</Trans>
          </Button>
        )}
      </MapperModal>
      <Loader loading={fetching}>
        <>
          <Table>
            <thead>
              <tr>
                <th>{label} Name</th>
                <th>Created</th>
                <th>Updated</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {externalMapperValues.map((externalMapperValue: ExternalMapperValue) => (
                <MapperRow
                  key={externalMapperValue.id}
                  externalMapper={externalMapper}
                  externalMapperValue={externalMapperValue}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                />
              ))}
            </tbody>
          </Table>
          <Pagination {...pagination} onClick={onPageClick} />
          {externalMapperValues.length === 0 && (
            <Warning>
              <strong>No results!</strong> No items to display for {label}.
            </Warning>
          )}
        </>
      </Loader>
    </div>
  );
};

export default JobMappings;
